import React, { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Calendar } from 'primereact/calendar';
import { Menu } from 'primereact/menu';
import { Tooltip } from 'primereact/tooltip';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import LabelName from 'components/UI/LabelName/LabelName';
import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import moment from 'moment-timezone';
import { roundToNextHalfHour } from 'utils/utility_functions/timezone';

function EventTime({ control, trigger, errors, setValue, timezoneBasedTime, startTimeWatcher, getValues, watch }) {
  const { editEventModal } = useCalendarContext();
  const { userDetails } = useUserDetailsContext();

  const recurrenceLimitWatcher = watch('recurrence_keys.recurrence_limit');
  const isRecurringWatcher = watch('is_recurring');

  useEffect(() => {
    if (!editEventModal) {
      setValue('meeting_start_time', roundToNextHalfHour(new Date(timezoneBasedTime)));
      addTimeHandler(roundToNextHalfHour(new Date(timezoneBasedTime)), 30, 'meeting_end_time');
    }
  }, []);

  useEffect(() => {
    const whenDate = new Date(getValues('when'));
    const startTime = new Date(getValues('meeting_start_time'));
    const endTime = new Date(getValues('meeting_end_time'));

    if (whenDate && startTime && endTime) {
      const updatedStartTime = new Date(whenDate);
      updatedStartTime.setHours(startTime.getHours());
      updatedStartTime.setMinutes(startTime.getMinutes());
      updatedStartTime.setSeconds(startTime.getSeconds());

      const updatedEndTime = new Date(whenDate);
      updatedEndTime.setHours(endTime.getHours());
      updatedEndTime.setMinutes(endTime.getMinutes());
      updatedEndTime.setSeconds(endTime.getSeconds());

      setValue('meeting_start_time', updatedStartTime);
      setValue('meeting_end_time', updatedEndTime);
    }
  }, [watch('when')]);

  const addTimePanel = useRef(null);
  const items = [
    // {
    //   label: '15 Minutes',
    //   command: () => {
    //     // addTimeHandler(endTimeWatcher, 15, "meeting_end_time");
    //     addTimeHandler(startTimeWatcher, 15, 'meeting_end_time');
    //   },
    // },
    {
      label: '30 Minutes',
      command: () => {
        // addTimeHandler(endTimeWatcher, 30, "meeting_end_time");
        addTimeHandler(startTimeWatcher, 30, 'meeting_end_time');
      },
    },
    {
      label: '1 Hour',
      command: () => {
        // addTimeHandler(endTimeWatcher, 60, "meeting_end_time");
        addTimeHandler(startTimeWatcher, 60, 'meeting_end_time');
      },
    },
    {
      label: '2 Hours',
      command: () => {
        // addTimeHandler(endTimeWatcher, 120, "meeting_end_time");
        addTimeHandler(startTimeWatcher, 120, 'meeting_end_time');
      },
    },
  ];

  function addTimeHandler(time, extraTime, field) {
    if (time) {
      let new_time = new Date(time);
      let addedTime = new_time.setMinutes(new_time.getMinutes() + extraTime);
      let endTime = new Date(addedTime);
      setValue(field, endTime);
      trigger('meeting_end_time');
    }
  }

  const getDateInTimezone = (date) => {
    return date ? moment.tz(date, userDetails?.timezone).toDate() : null;
  };

  return (
    <div className="d-flex align-items-center py-2 flex-wrap">
      <div className="col-md-4 col-12">
        <LabelName required={true}>Time</LabelName>
      </div>
      <div className="col-md-8 col-12 d-flex justify-content-between align-items-center">
        <div className="w-50">
          <Controller
            name="meeting_start_time"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={getDateInTimezone(field.value)}
                // onChange={(e) => {
                //   field.onChange(e.value);
                //   trigger('meeting_start_time');
                //   addTimeHandler(e.value, 30, 'meeting_end_time');
                // }}
                onChange={(e) => {
                  field.onChange(e.value);

                  trigger('meeting_start_time');
                  const allValues = getValues();
                  const whenDate = new Date(allValues?.when);
                  const startTime = new Date(e.value);
                  whenDate.setHours(startTime.getHours());
                  whenDate.setMinutes(startTime.getMinutes());
                  whenDate.setSeconds(startTime.getSeconds());

                  setValue('meeting_start_time', whenDate);
                  // By default, set end time to 30 minutes after the new start time
                  const defaultEndTime = new Date(whenDate);
                  defaultEndTime.setMinutes(defaultEndTime.getMinutes() + 30);
                  setValue('meeting_end_time', defaultEndTime);

                  const tillDate = new Date(recurrenceLimitWatcher);
                  tillDate.setHours(startTime.getHours());
                  tillDate.setMinutes(startTime.getMinutes());
                  tillDate.setSeconds(startTime.getSeconds());
                  if (isRecurringWatcher) {
                    setValue('recurrence_keys.recurrence_limit', tillDate);
                  }
                }}
                timeOnly
                hourFormat="12"
                stepMinute={30}
                // minDate={new Date(whenWatcher)}
                placeholder="Start Time"
                className="input-shadow w-100 "
                inputRef={field.ref}
              />
            )}
          />
        </div>

        <div className="w-50 me-2">
          <Controller
            name="meeting_end_time"
            className="w-100"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={getDateInTimezone(field.value)}
                // onChange={(e) => {
                //   field.onChange(e.value);
                //   trigger('meeting_end_time');
                // }}
                onChange={(e) => {
                  field.onChange(e.value);
                  trigger('meeting_end_time');
                  const allValues = getValues();
                  const whenDate = new Date(allValues?.when);
                  const endTime = new Date(e.value);
                  whenDate.setHours(endTime.getHours());
                  whenDate.setMinutes(endTime.getMinutes());
                  whenDate.setSeconds(endTime.getSeconds());

                  // Check if end time is before start time
                  const startTime = new Date(getValues('meeting_start_time'));
                  if (startTime && whenDate <= startTime) {
                    // If end time is before or equal to start time, move it to the next day
                    const nextDayEndTime = new Date(whenDate);
                    nextDayEndTime.setDate(nextDayEndTime.getDate() + 1);
                    setValue('meeting_end_time', nextDayEndTime);
                  } else {
                    setValue('meeting_end_time', whenDate);
                  }
                }}
                timeOnly
                hourFormat="12"
                stepMinute={30}
                // minDate={startTimeWatcher ?? new Date(timezoneBasedTime)}
                placeholder="End Time"
                className="input-shadow w-100 mx-2"
                inputRef={field.ref}
              />
            )}
          />
        </div>
        {startTimeWatcher && (
          <>
            <Tooltip position="right" target="#duration" content={`Time Duration`} style={{ marginLeft: '10px' }} />
            <i
              id="duration"
              label="+"
              className="ms-3 fas fa-plus-circle pointer"
              onClick={(event) => addTimePanel.current.toggle(event)}
              aria-controls="add_time_panel"
              aria-haspopup
            />
            <Menu model={items} popup ref={addTimePanel} id="add_time_panel" />
          </>
        )}
      </div>
      <div className="offset-md-4">
        {errors?.meeting_start_time && <InputErrorMessage>Please select event start time</InputErrorMessage>}
      </div>
      <div>{errors?.meeting_end_time && <InputErrorMessage>Please select event end time</InputErrorMessage>}</div>
    </div>
  );
}

export default EventTime;
